import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'components/InfiniteScroll';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import ChatMessage from './components/ChatMessage';
import { MessageType, MESSAGE_STATUS_INDICATOR } from 'models/Message';
import { focusElement, setDatesToMessages } from './helpers';
import useChatListener from './hooks/useChatListeners';
import useChatScroll from './hooks/useChatScroll';
import useCallbackRef from 'hooks/useCallbackRef';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import uniqBy from 'lodash/uniqBy';
import ResolveImage from 'components/ResolveImage';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';
import api from 'api';
import { getImgUrl } from 'components/ResolveImage/utils';
import { getUserFullName } from 'utils';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { MessageForm } from 'components/MessageForm';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useUploadMessageFile from 'hooks/useUploadMessageFile';
import MessagesEmptyState from './components/MessagesEmptyState';
import { AxiosRequestConfig } from 'axios';
import useBusEvent from 'hooks/useBusEvent';
import MessagesEmptyImage from 'assets/images/market-a-listing.webp';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import {
  MessageDeletedEventData,
  MessageSeenEventData,
} from 'services/socketService/socketEventData';
import { trimRichTextValueFromJson } from 'components/RichTextEditorField/utils';

import './Chat.styles.scss';
import './Chat.styles.responsive.scss';

export const UserPhotoPlaceholder: React.FC<IContentLoaderProps> = (props) => (
  <ContentLoader
    style={{ width: '120px', height: '69px' }}
    backgroundColor="#e7f1fc"
    foregroundColor="#ffffff"
    preserveAspectRatio="none"
    {...props}
  >
    <circle cx="60" cy="20" r="20" />;
    <rect x="0" y="48" rx="8" ry="8" width="120px" height="16" />
  </ContentLoader>
);

type ChatProps = {
  showUserData?: boolean;
  showButtons?: boolean;
  showEmptyState?: boolean;
};

const Chat: React.FC<ChatProps> = (props) => {
  const { showUserData = true, showButtons = true, showEmptyState } = props;

  const { currentUser } = useContext(CurrentUserContext);
  const {
    interlocutor,
    currentSession,
    loadingSession,
    sessionId,
    setContactOpen,
    setContactsOpen,
  } = useContext(MessagingContext);

  const {
    listing,
    isExternalCommunication,
    isBuyer,
    buyerId,
    isClosed,
    leadWithdrawnInterest,
  } = currentSession || {};

  const { t } = useTranslation();

  const classes = classNames('chat');

  const onUploadReq = useCallback(
    (file: File, config: AxiosRequestConfig) =>
      api.messages.uploadFile(sessionId, file, config),
    [sessionId],
  );

  const {
    files,
    uploadedFiles,
    areAllFilesProcessed,
    restRootProps,
    clearFiles,
    onFileRemove,
    onFileUpload,
    getInputProps,
  } = useUploadMessageFile({
    onUploadReq,
  });

  const currentUserImg = useMemo(
    () => getImgUrl(currentUser.profileImage?.url, '100'),
    [currentUser],
  );
  const companyLogoImg = useMemo(
    () => getImgUrl(listing?.dealRoom?.companyLogo?.url, '100'),
    [listing],
  );
  const interlocutorImg = useMemo(
    () => getImgUrl(interlocutor?.profileImage?.url, '100'),
    [interlocutor?.profileImage?.url],
  );

  const dataRoomName = useMemo(
    () => listing?.dealRoom?.name,
    [listing?.dealRoom?.name],
  );

  const dataRoomId = useMemo(
    () => listing?.dealRoom?.dataRoomId,
    [listing?.dealRoom?.dataRoomId],
  );

  const [newMessageSent, setNewMessageSent] = useState<boolean>(false);
  const [hasNewMessage, setHasNewMessage] = useState<boolean>(false);

  const [messagesContainer, messagesContainerRef] =
    useCallbackRef<InfiniteScrollRef>();

  const forbidMessaging =
    leadWithdrawnInterest || isExternalCommunication || isClosed;

  const {
    loading,
    items: messages,
    setItems: setMessages,
    onContainerScrolled: onMessagesScroll,
  } = useInfinitePagination<MessageType>({
    condition: !!sessionId,
    resetItemsWhenReset: false,
    resetDeps: [sessionId],
    dependencies: [sessionId],
    makeRequest: async (currentPage: number) => {
      return api.messages
        .getSessionMessages(
          {
            $page: currentPage.toString(),
            $limit: '50',
            $sort: '-createdAt',
          },
          sessionId,
        )
        .then((res) => res.data);
    },
    onResetDeps: () => (sentMessages.current = []),
    onItemsLoad: (cp, items) => {
      // when it is scrollable and current page not first one
      // then scroll to bottom to see loaded messages
      if (shouldScrollTL && cp === 1) scrollToView();
      sentMessages.current = uniqBy([...sentMessages.current, ...items], 'id');
    },
  });

  const sentMessages = useRef<Array<MessageType>>(messages);
  const finalMessages = useMemo(
    () =>
      setDatesToMessages(messages).filter((msg) => msg.sessionId === sessionId),
    [messages, sessionId],
  );

  const { showSTL, shouldScrollTL, scrollToBottom } = useChatScroll({
    condition: hasNewMessage,
    container: messagesContainer?.container as HTMLDivElement,
  });

  const loadingSpinner = useMemo(
    () => (loading || !currentSession ? <Loader overlay={false} /> : null),
    [currentSession, loading],
  );

  const interlocutorFullName = useMemo(
    () => getUserFullName(interlocutor?.firstName, interlocutor?.lastName),
    [interlocutor?.firstName, interlocutor?.lastName],
  );

  const handleMessageArrived = useCallback(
    (message: MessageType) => {
      // if user scrolled container above last message
      // and new message arrived then set flag for showSTL(show scroll to latest)
      sentMessages.current = [message, ...messages];

      if (shouldScrollTL && sessionId === message.sessionId) {
        setHasNewMessage(true);
        api.messages.readAllMessages(sessionId);
      }
    },
    [messages, sessionId, shouldScrollTL],
  );

  useChatListener({
    setMessages,
    onMessageArrived: handleMessageArrived,
  });

  const scrollToView = useCallback(() => {
    setHasNewMessage(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const getCreatedBy = useCallback(
    (userId) => {
      if (userId === currentUser?._id) return t('you');

      if (buyerId === userId) return interlocutorFullName;

      return listing?.dealRoom?.companyName;
    },
    [
      buyerId,
      currentUser?._id,
      interlocutorFullName,
      listing?.dealRoom?.companyName,
      t,
    ],
  );

  const handleOnSendMessage = useCallback(
    async (message: string) => {
      const trimmedMessage = trimRichTextValueFromJson(message);

      const { data: sentMessage } = await api.messages.sendMesssage(
        sessionId,
        trimmedMessage,
        uploadedFiles,
      );

      setMessages((oldMessages) => [sentMessage, ...oldMessages]);

      const { createdChatSessionFiles } = sentMessage;

      sentMessages.current = [sentMessage, ...sentMessages.current];
      setNewMessageSent(true);
      clearFiles();

      // event message that has been sent in current session
      if (shouldScrollTL) scrollToView();
      bus.broadcastEvent(StandardEventTypes.MESSAGE_SENT, sentMessage);

      if (createdChatSessionFiles?.length)
        bus.broadcastEvent(
          StandardEventTypes.FILES_SENT,
          createdChatSessionFiles,
        );
      focusElement('#message-form-input');
    },
    [
      sessionId,
      uploadedFiles,
      shouldScrollTL,
      clearFiles,
      setMessages,
      scrollToView,
    ],
  );

  const handleOnError = useCallback(
    () =>
      setMessages((oldMessages) =>
        oldMessages.map((message, index) =>
          index === 0
            ? {
                ...message,
                statusIndicator: MESSAGE_STATUS_INDICATOR.ERROR,
              }
            : message,
        ),
      ),
    [setMessages],
  );

  const onFileDelete = useCallback(
    (data: CallbackBusType<string>) => {
      const { payload } = data;

      setMessages((old) =>
        old.map((el) => ({
          ...el,
          files: el.files.filter(
            ({ chatSessionFileId }) => chatSessionFileId !== payload,
          ),
        })),
      );
    },
    [setMessages],
  );

  useBusEvent(StandardEventTypes.FILE_DELETE, onFileDelete);

  const updateDeletedMessage = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m._id !== messageId) return m;

          if (m.files.length) {
            m.files.forEach((file) =>
              bus.broadcastEvent(StandardEventTypes.FILE_DELETED_WITH_MESSAGE, {
                chatSessionFileId: file.chatSessionFileId,
              }),
            );
          }

          return { ...m, isDeleted: true, text: '', files: [] };
        }),
      );
    },
    [setMessages],
  );

  const onDeleteMessageRequest = useCallback(
    async (messageId: string) => {
      api.messages.deleteMessage(messageId);
      updateDeletedMessage(messageId);
    },
    [updateDeletedMessage],
  );

  const onDeleteMessageEvent = useCallback(
    ({ sessionId: payloadSessionId, messageId }: MessageDeletedEventData) => {
      if (sessionId !== payloadSessionId) return;

      updateDeletedMessage(messageId);
    },
    [sessionId, updateDeletedMessage],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_DELETED, onDeleteMessageEvent);

  const onSeenMessageEvent = useCallback(
    ({ sessionId: payloadSessionId, seenAt }: MessageSeenEventData) => {
      if (sessionId !== payloadSessionId) return;

      setMessages((prev) =>
        prev.map((m) => (m?.seenAt ? m : { ...m, seenAt })),
      );
    },
    [sessionId, setMessages],
  );

  const updateMessage = useCallback(
    (message: Partial<MessageType>) =>
      setMessages((prev) =>
        prev.map((m) => (message._id === m._id ? { ...m, ...message } : m)),
      ),
    [setMessages],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_SEEN, onSeenMessageEvent);

  useEffect(() => {
    if (!newMessageSent) return;
    // this effect will be executed when new message is sent
    // old messages will be replaced with sent one and
    // status of each will update

    setMessages(sentMessages.current);
    setNewMessageSent(false);
  }, [newMessageSent, setMessages]);

  return (
    <div className={classes} {...restRootProps}>
      <input {...getInputProps()} />
      {showUserData && (
        <div className="chat__user-data">
          {showButtons && (
            <Button
              variant="icon"
              className="chat-arrow"
              icon={ArrowleftIcon}
              onClick={() => setContactsOpen((old) => !old)}
              aria-label="Show all chats."
            />
          )}

          <div className="chat__user-data__info">
            {loadingSession || !currentSession ? (
              <UserPhotoPlaceholder />
            ) : (
              <>
                <div className="chat__user-data__image">
                  <ResolveImage
                    asAvatar
                    src={isBuyer ? companyLogoImg : interlocutorImg}
                  />
                </div>
                <p className="chat__users-data__name">
                  {isBuyer
                    ? listing?.dealRoom?.companyName
                    : interlocutorFullName}
                </p>
              </>
            )}
          </div>

          {showButtons && (
            <Button
              variant="icon"
              className="chat-arrow"
              icon={ArrowrightIcon}
              onClick={() => setContactOpen((old) => !old)}
              aria-label="Show contact details."
            />
          )}
        </div>
      )}

      {(isExternalCommunication || isClosed) && (
        <p className="chat__banner">
          <LockclosedIcon />
          {t(isClosed ? 'bannerClosedSession' : 'bannerFor3rdPary')}
        </p>
      )}

      <InfiniteScroll
        ref={messagesContainerRef}
        className="chat__messages"
        localScroll
        onScroll={onMessagesScroll}
      >
        {showSTL && (
          <div className="chat__scroll-to-bottom" onClick={scrollToView}>
            {t('latestMessage')}
          </div>
        )}

        {!loading &&
          currentSession &&
          finalMessages.map(({ _id, userId, ...message }, index) => (
            <ChatMessage
              key={_id}
              messageId={_id}
              buyerId={buyerId}
              isBuyer={isBuyer}
              dataRoomId={dataRoomId}
              dataRoomName={dataRoomName}
              userId={userId}
              currentUserImg={currentUserImg}
              companyLogoImg={companyLogoImg}
              interlocutorImg={interlocutorImg}
              disabled={forbidMessaging}
              createdBy={getCreatedBy(userId)}
              prevMsgUserId={index > 0 ? finalMessages[index - 1].userId : ''}
              onDeleteMessageRequest={onDeleteMessageRequest}
              updateMessage={updateMessage}
              {...message}
            />
          ))}

        {showEmptyState && !loadingSpinner && !finalMessages.length && (
          <MessagesEmptyState
            text={t('emptyStateChat', {
              name: interlocutorFullName,
            })}
            description={t('sendFirstMessage')}
            imageSrc={MessagesEmptyImage}
          />
        )}

        {loadingSpinner}
      </InfiniteScroll>

      <MessageForm
        disabled={forbidMessaging}
        placeholder={t(forbidMessaging ? 'youCantReply' : 'writeAMessage')}
        resetMessage={sessionId}
        uploadFileHintMessage={t('hintYouCanDragFilesToChat')}
        buttonText={t('send')}
        files={files}
        areAllFilesProcessed={areAllFilesProcessed}
        onFileUpload={onFileUpload}
        onFileRemove={onFileRemove}
        handleOnError={handleOnError}
        handleOnSubmit={handleOnSendMessage}
      >
        {isExternalCommunication && (
          <p>
            <LockclosedIcon />
            {t('youCantReplyTo3rdPart')}
          </p>
        )}
      </MessageForm>
    </div>
  );
};

export default Chat;
